// Accessibility utilities for Atlas IDE

// Generate unique IDs for ARIA relationships
let idCounter = 0;
export function generateAriaId(prefix: string = 'aria'): string {
  return `${prefix}-${++idCounter}`;
}

// Common ARIA role mappings for IDE components
export const AriaRoles = {
  // Main layout
  APPLICATION: 'application',
  MAIN: 'main',
  NAVIGATION: 'navigation',
  COMPLEMENTARY: 'complementary',
  REGION: 'region',

  // Interactive elements
  BUTTON: 'button',
  MENUBAR: 'menubar',
  MENU: 'menu',
  MENUITEM: 'menuitem',
  TAB: 'tab',
  TABLIST: 'tablist',
  TABPANEL: 'tabpanel',
  TREE: 'tree',
  TREEITEM: 'treeitem',
  LISTBOX: 'listbox',
  OPTION: 'option',

  // Status and feedback
  STATUS: 'status',
  ALERT: 'alert',
  PROGRESSBAR: 'progressbar',
  LOG: 'log',

  // Content
  DIALOG: 'dialog',
  ALERTDIALOG: 'alertdialog',
  TOOLTIP: 'tooltip',
  TOOLBAR: 'toolbar',
  SEARCH: 'search',
  GROUP: 'group',
  SEPARATOR: 'separator',
} as const;

// Common ARIA labels for IDE components
export const AriaLabels = {
  // Main layout
  SIDEBAR: 'File explorer sidebar',
  EDITOR: 'Code editor',
  PREVIEW: 'Live preview',
  CONSOLE: 'Output console',
  TERMINAL: 'Terminal',
  PROBLEMS: 'Problems panel',
  NETWORK: 'Network requests',

  // File explorer
  FILE_TREE: 'Project file tree',
  FILE_FILTER: 'Filter files',
  NEW_FILE: 'Create new file',
  NEW_FOLDER: 'Create new folder',
  REFRESH: 'Refresh file tree',
  COLLAPSE_ALL: 'Collapse all folders',
  EXPAND_ALL: 'Expand all folders',

  // Editor
  EDITOR_TABS: 'Open files tabs',
  CLOSE_TAB: 'Close tab',
  CLOSE_ALL_TABS: 'Close all tabs',
  SAVE_FILE: 'Save file',
  UNDO: 'Undo',
  REDO: 'Redo',
  SEARCH: 'Search in file',
  SEARCH_REPLACE: 'Search and replace',
  FORMAT_CODE: 'Format code',
  FOLD_ALL: 'Fold all',
  UNFOLD_ALL: 'Unfold all',

  // Preview
  REFRESH_PREVIEW: 'Refresh preview',
  OPEN_IN_NEW_TAB: 'Open in new tab',
  DEVICE_SELECTOR: 'Select device',
  ORIENTATION: 'Toggle orientation',
  ZOOM: 'Zoom level',
  SCREENSHOT: 'Take screenshot',
  RECORD: 'Record video',

  // Git
  SOURCE_CONTROL: 'Source control',
  STAGE_FILE: 'Stage file',
  UNSTAGE_FILE: 'Unstage file',
  COMMIT: 'Commit changes',
  PUSH: 'Push to remote',
  PULL: 'Pull from remote',
  BRANCH_SELECTOR: 'Select branch',

  // Status
  STATUS_BAR: 'Status bar',
  LINE_COLUMN: 'Line and column indicator',
  LANGUAGE_MODE: 'Language mode',
  ENCODING: 'File encoding',

  // Modals
  CLOSE_MODAL: 'Close modal',
  SEARCH_INPUT: 'Search input',
  COMMAND_INPUT: 'Command input',
} as const;

// Keyboard shortcut descriptions for screen readers
export const KeyboardHints = {
  SAVE: 'Press Command+S to save',
  UNDO: 'Press Command+Z to undo',
  REDO: 'Press Command+Shift+Z to redo',
  SEARCH: 'Press Command+F to search',
  REPLACE: 'Press Command+H to replace',
  GLOBAL_SEARCH: 'Press Command+Shift+F to search all files',
  GO_TO_FILE: 'Press Command+P to go to file',
  COMMAND_PALETTE: 'Press Command+Shift+P for command palette',
  GO_TO_LINE: 'Press Control+G to go to line',
  GO_TO_SYMBOL: 'Press Command+Shift+O to go to symbol',
  CLOSE_TAB: 'Press Command+W to close tab',
  NEW_FILE: 'Press Command+N to create new file',
  TOGGLE_SIDEBAR: 'Press Command+B to toggle sidebar',
  TOGGLE_TERMINAL: 'Press Control+Backtick to toggle terminal',
  FOCUS_EDITOR: 'Press Escape to focus editor',
} as const;

// Announce to screen readers using ARIA live regions
export function announceToScreenReader(message: string, priority: 'polite' | 'assertive' = 'polite'): void {
  const announcement = document.getElementById('aria-live-region');
  if (announcement) {
    announcement.setAttribute('aria-live', priority);
    announcement.textContent = message;
    // Clear after announcement
    setTimeout(() => {
      announcement.textContent = '';
    }, 1000);
  }
}

// Create the live region element (call once on app init)
export function createAriaLiveRegion(): HTMLDivElement {
  const existing = document.getElementById('aria-live-region');
  if (existing) return existing as HTMLDivElement;

  const region = document.createElement('div');
  region.id = 'aria-live-region';
  region.setAttribute('aria-live', 'polite');
  region.setAttribute('aria-atomic', 'true');
  region.style.cssText = `
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  `;
  document.body.appendChild(region);
  return region;
}

// Focus trap utility for modals
export function createFocusTrap(container: HTMLElement): () => void {
  const focusableSelector = `
    a[href],
    button:not([disabled]),
    input:not([disabled]),
    select:not([disabled]),
    textarea:not([disabled]),
    [tabindex]:not([tabindex="-1"])
  `;

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;

    const focusable = Array.from(
      container.querySelectorAll<HTMLElement>(focusableSelector)
    );

    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  container.addEventListener('keydown', handleKeyDown);
  return () => container.removeEventListener('keydown', handleKeyDown);
}

// Skip link component props
export type SkipLinkProps = {
  target: string;
  label?: string;
};

// Generate props for accessible buttons
export function getButtonProps(label: string, description?: string) {
  return {
    'aria-label': label,
    ...(description && { 'aria-describedby': generateAriaId('desc') }),
    role: 'button' as const,
  };
}

// Generate props for tree items
export function getTreeItemProps(
  label: string,
  expanded: boolean | undefined,
  selected: boolean,
  level: number
) {
  return {
    role: 'treeitem' as const,
    'aria-label': label,
    'aria-expanded': expanded,
    'aria-selected': selected,
    'aria-level': level,
  };
}

// Generate props for tabs
export function getTabProps(id: string, label: string, selected: boolean, panelId: string) {
  return {
    role: 'tab' as const,
    id,
    'aria-label': label,
    'aria-selected': selected,
    'aria-controls': panelId,
    tabIndex: selected ? 0 : -1,
  };
}

// Generate props for tab panels
export function getTabPanelProps(id: string, labelledBy: string, hidden: boolean) {
  return {
    role: 'tabpanel' as const,
    id,
    'aria-labelledby': labelledBy,
    hidden,
    tabIndex: 0,
  };
}
